/**
 * Commander Design System - Colour Palette
 *
 * IBM Carbon-inspired neutral palette with purposeful accent colours.
 * Designed for enterprise readability and professional aesthetics.
 */

export const gray = {
  10: '#f4f4f4',
  20: '#e0e0e0',
  30: '#c6c6c6',
  40: '#a8a8a8',
  50: '#8d8d8d',
  60: '#6f6f6f',
  70: '#525252',
  80: '#393939',
  90: '#262626',
  100: '#161616',
} as const;

export const blue = {
  10: '#edf5ff',
  20: '#d0e2ff',
  30: '#a6c8ff',
  40: '#78a9ff',
  50: '#4589ff',
  60: '#0f62fe',
  70: '#0043ce',
  80: '#002d9c',
  90: '#001d6c',
  100: '#001141',
} as const;

export const green = {
  10: '#defbe6',
  20: '#a7f0ba',
  30: '#6fdc8c',
  40: '#42be65',
  50: '#24a148',
  60: '#198038',
  70: '#0e6027',
  80: '#044317',
  90: '#022d0d',
  100: '#071908',
} as const;

export const red = {
  10: '#fff1f1',
  20: '#ffd7d9',
  30: '#ffb3b8',
  40: '#ff8389',
  50: '#fa4d56',
  60: '#da1e28',
  70: '#a2191f',
  80: '#750e13',
  90: '#520408',
  100: '#2d0709',
} as const;

export const yellow = {
  10: '#fcf4d6',
  20: '#fddc69',
  30: '#f1c21b',
  40: '#d2a106',
  50: '#b28600',
  60: '#8e6a00',
  70: '#684e00',
  80: '#483700',
  90: '#302400',
  100: '#1c1500',
} as const;

export const status = {
  /** Green - Equipment operational and available */
  online: green[50],
  /** Grey - Equipment not responding or unavailable */
  offline: gray[50],
  /** Red - Equipment restricted/stunned */
  stunned: red[60],
  /** Yellow - Requires attention (overdue, low battery, etc.) */
  warning: yellow[30],
  /** Blue - Currently in use (checked out) */
  active: blue[60],
} as const;

export const semantic = {
  /** Primary surface background */
  background: gray[10],
  /** Card and elevated surfaces */
  surface: '#ffffff',
  /** Subtle surface for alternating rows */
  surfaceAlt: gray[10],
  /** Primary border colour */
  border: gray[20],
  /** Disabled state border */
  borderDisabled: gray[30],
  /** Primary text colour */
  textPrimary: gray[80],
  /** Secondary/muted text */
  textSecondary: gray[60],
  /** Disabled text */
  textDisabled: gray[30],
  /** Headings */
  textHeading: gray[100],
  /** Primary action colour */
  primary: blue[60],
  /** Primary hover state */
  primaryHover: blue[70],
  /** Success/confirmation */
  success: green[50],
  /** Error/danger */
  error: red[60],
  /** Warning/attention */
  warning: yellow[30],
} as const;

export const colours = {
  gray,
  blue,
  green,
  red,
  yellow,
  status,
  semantic,
} as const;

export type ColourScale = typeof gray | typeof blue | typeof green | typeof red | typeof yellow;
export type StatusColour = keyof typeof status;
export type SemanticColour = keyof typeof semantic;
